// hide-on-scroll.directive.ts
import { Directive, ElementRef, HostBinding, OnDestroy, OnInit, inject, input, PLATFORM_ID, Renderer2 } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * hide on scroll down and show on scroll up
 */
@Directive({
  selector: '[hideOnScrollDown]',
  standalone: true,
  host: {
    '[class.is-hidden]': 'isHidden',
    '[style.transition]': '"transform 0.3s ease-in-out"',
    '[style.will-change]': '"transform"',
    '[style.position]': '"fixed"',
    '[style.top]': '"0"',
    '[style.left]': '"0"',
    '[style.right]': '"0"',
    '[style.z-index]': '"50"',
  },
})
export class HideOnScrollDownDirective implements OnInit, OnDestroy {
  /** Minimum scroll distance (px) before the header reacts */
  readonly threshold = input(10);
  /** Offset from the top after which hiding is allowed */
  readonly topOffset = input(0);
  /** Optional fixed height for the spacer (e.g. "100px").  
      If omitted, the real header height is measured. */
  readonly spacerHeight = input<string | null>(null);

  isHidden = false;

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly renderer = inject(Renderer2);

  private lastScrollY = 0;
  private ticking = false;
  private scrollListener: (() => void) | null = null;
  private styleElement: HTMLStyleElement | null = null;
  private spacer: HTMLElement | null = null;

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.injectStyles();
    this.createSpacer();
    this.lastScrollY = window.scrollY;

    this.scrollListener = () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => this.onScroll());
        this.ticking = true;
      }
    };

    window.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  ngOnDestroy(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }
  }

  private onScroll(): void {
    const currentScrollY = window.scrollY;
    const delta = currentScrollY - this.lastScrollY;

    if (Math.abs(delta) < this.threshold()) {
      this.ticking = false;
      return;
    }

    const headerHeight = this.el.nativeElement.offsetHeight;

    if (delta > 0 && currentScrollY > headerHeight + this.topOffset()) {
      // scrolling down → hide
      this.isHidden = true;
    } else {
      // scrolling up → show
      this.isHidden = false;
    }

    this.lastScrollY = currentScrollY;
    this.ticking = false;
  }

  private injectStyles(): void {
    const styleId = 'hide-on-scroll-down-styles';

    if (document.getElementById(styleId)) {
      return; // already injected
    }

    this.styleElement = document.createElement('style');
    this.styleElement.id = styleId;
    this.styleElement.textContent = `
      [hideOnScrollDown].is-hidden {
        transform: translateY(-100%);
      }
    `;
    document.head.appendChild(this.styleElement);
  }

  private createSpacer(): void {
    const host = this.el.nativeElement as HTMLElement;
    const parent = host.parentNode;

    if (!parent) {
      return;
    }

    this.spacer = this.renderer.createElement('div');
    this.renderer.setAttribute(this.spacer, 'aria-hidden', 'true');
    this.renderer.addClass(this.spacer, 'app-hide-on-scroll-spacer');

    // Set height
    if (this.spacerHeight()) {
      this.renderer.setStyle(this.spacer, 'height', this.spacerHeight());
    } else {
      // Measure after a microtask so the header has its final size
      queueMicrotask(() => {
        const height = host.getBoundingClientRect().height;
        this.renderer.setStyle(this.spacer!, 'height', `${height}px`);
      });
    }

    // Insert the spacer immediately before the header
    this.renderer.insertBefore(parent, this.spacer, host);
  }
}
